import {ModalComponentProps, Block, Button, Card, Checkbox, Dialog, DialogBody, DialogFooter, Text} from '@mc/ui-v4';

import React, {useState} from 'react';
import {JsonImportCheckVO} from '../../../api';
import {JSON_IMPORT_TITLE} from './UploadJsonModal';

type Props = ModalComponentProps & {
    data: JsonImportCheckVO;
    onConfirm: (importNew: boolean, updateExisting: boolean) => Promise<void>;
};

/**
 * Zobrazí, kolik objektů se ze souboru vloží a kolik aktualizuje (ztotožnění přes externí id),
 * a nechá uživatele vybrat, které z obou akcí se mají provést. Obě volby jsou výchozím stavem vypnuté.
 */
export const ConfirmJsonImportModal: React.FC<Props> = ({onClose, data, onConfirm}) => {
    const [submitting, setSubmitting] = useState(false);
    const [importNew, setImportNew] = useState(false);
    const [updateExisting, setUpdateExisting] = useState(false);

    const newCount = data.newCount ?? 0;
    const updateCount = data.updateCount ?? 0;
    // Nová vrstva ještě žádné objekty nemá, aktualizovat tedy není co
    const novaVrstva = !data.layerId;
    const hasDuplicates = !!data.duplicateIdCount && data.duplicateIdCount > 0;

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await onConfirm(importNew, updateExisting);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog onClose={onClose} title={JSON_IMPORT_TITLE}>
            <DialogBody>
                <Card>
                    <Block>
                        {hasDuplicates ? (
                            <Text intent={'danger'}>Soubor obsahuje {data.duplicateIdCount} duplicitních id, import nelze provést.</Text>
                        ) : (
                            <>
                                <Text>Objektů v souboru: {data.recordCount ?? 0}</Text>
                                {!!data.recordsWithoutId && data.recordsWithoutId > 0 && (
                                    <Text intent={'secondary'}>Objektů bez id (budou přeskočeny): {data.recordsWithoutId}</Text>
                                )}
                                {!!data.recordsWithoutGeometry && data.recordsWithoutGeometry > 0 && (
                                    <Text intent={'secondary'}>
                                        Objektů bez souřadnic (nelze je vložit): {data.recordsWithoutGeometry}
                                    </Text>
                                )}
                                <div className="mt-3">
                                    <Checkbox
                                        label={`Vložit nové objekty (${newCount})`}
                                        checked={importNew}
                                        disabled={submitting || newCount === 0}
                                        onChange={() => setImportNew(!importNew)}
                                    />
                                    <Checkbox
                                        label={`Aktualizovat existující objekty (${updateCount})`}
                                        checked={updateExisting}
                                        disabled={submitting || novaVrstva || updateCount === 0}
                                        onChange={() => setUpdateExisting(!updateExisting)}
                                    />
                                </div>
                            </>
                        )}
                    </Block>
                </Card>
            </DialogBody>
            <DialogFooter>
                <Button disabled={submitting} style={'link'} onClick={onClose}>
                    Zrušit
                </Button>
                <Button
                    style={'primary'}
                    disabled={submitting || !data.canProceed || (!importNew && !updateExisting)}
                    onClick={handleConfirm}
                >
                    Spustit import
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
